'use client';

import { useMemo } from 'react';
import {
	BigNum,
	BN,
	QUOTE_PRECISION_EXP,
	SPOT_MARKET_RATE_PRECISION,
	SPOT_MARKET_RATE_PRECISION_EXP,
	SpotBalanceType,
} from '@velocity-exchange/sdk';
import useAccountSpotBalances from 'src/hooks/useAccountBankBalances';
import { NumLib } from '@velocity-exchange/common';
import { useAllMarketInterestData } from 'src/hooks/earn/useAllMarketInterestData';
import useCurrentOrIsolatedPoolUserKey from 'src/hooks/useCurrentOrIsolatedPoolUserKey';

interface Params {
	poolId: number;
}

export default function useAvgDepositApy({ poolId }: Params) {
	const { data: allMarketData } = useAllMarketInterestData();
	const userKeyForPool = useCurrentOrIsolatedPoolUserKey(poolId, true, true);

	const depositBalances = useAccountSpotBalances(
		userKeyForPool,
		SpotBalanceType.DEPOSIT,
		poolId
	);

	return useMemo(() => {
		const accountTotalDeposits = NumLib.sumBigNums(
			depositBalances
				.filter((bal) => bal.accountKey === userKeyForPool)
				.map((bal) => bal.quoteValue),
			QUOTE_PRECISION_EXP
		);

		if (!accountTotalDeposits || accountTotalDeposits.eqZero()) {
			return BigNum.zero();
		}

		const depositBalancesOfUser = depositBalances.filter(
			(bal) => bal.accountKey == userKeyForPool
		);

		return NumLib.sumBigNums(
			depositBalancesOfUser.map((bal) => {
				const proportionOfDeposits =
					accountTotalDeposits.toNum() >= 1
						? BigNum.from(
								(bal.quoteValue.toNum() / accountTotalDeposits.toNum()) *
									SPOT_MARKET_RATE_PRECISION.toNumber(),
								SPOT_MARKET_RATE_PRECISION_EXP
							)
						: BigNum.from(
								1 / depositBalances.length,
								SPOT_MARKET_RATE_PRECISION_EXP
							).mul(SPOT_MARKET_RATE_PRECISION);

				const marketInterestData = allMarketData?.[bal.asset.marketIndex];

				const depositApyBN =
					marketInterestData?.deposit.totalApyBN.div(new BN(100)) ??
					BigNum.zero();

				const bankDepositApy =
					depositApyBN?.mul(new BN(100)) ??
					BigNum.zero(SPOT_MARKET_RATE_PRECISION_EXP);

				const proportionValue = proportionOfDeposits.mul(bankDepositApy);

				return proportionValue.shiftTo(SPOT_MARKET_RATE_PRECISION_EXP);
			}),
			SPOT_MARKET_RATE_PRECISION_EXP
		);
	}, [depositBalances, allMarketData, userKeyForPool]);
}
